"use client";

import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import AnimatedWheelIcon from "@/app/_components/Icons/AnimatedWheelIcon";
import { Box, Card, CardActionArea, Icon, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import NextLink from "next/link";

const HREF = "/gardone-shans?utm_source=zibasi&utm_medium=banner";

const floatBlob = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-6px, 8px) scale(1.06); }
`;

const ctaPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(212, 129, 54, 0.35); }
  50% { box-shadow: 0 0 0 8px rgba(212, 129, 54, 0); }
`;

type Props = {
  variant: "desktop" | "mobile";
};

export default function WheelBentoCard({ variant }: Props) {
  const isDesktop = variant === "desktop";

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: isDesktop ? undefined : 200,
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        background: "linear-gradient(145deg, #FFE7D1 0%, #FFDFC2 45%, #F3BE8F 100%)",
      }}
    >
      <CardActionArea
        component={NextLink}
        href={HREF}
        aria-label="رفتن به گردونه شانس"
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Soft floating highlights */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: isDesktop ? -40 : -24,
            left: isDesktop ? -30 : -20,
            width: isDesktop ? 180 : 110,
            height: isDesktop ? 180 : 110,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.35)",
            animation: `${floatBlob} 5.5s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: isDesktop ? 40 : 20,
            right: isDesktop ? -50 : -36,
            width: isDesktop ? 220 : 140,
            height: isDesktop ? 220 : 140,
            borderRadius: "50%",
            bgcolor: "rgba(212, 129, 54, 0.18)",
            animation: `${floatBlob} 7s ease-in-out infinite reverse`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />

        {/* Wheel visual */}
        <Box
          sx={{
            position: "absolute",
            top: isDesktop ? 28 : 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
            filter: "drop-shadow(0 8px 18px rgba(80, 50, 20, 0.18))",
          }}
        >
          <AnimatedWheelIcon size={isDesktop ? 140 : 90} />
        </Box>

        {/* Copy + CTA */}
        <Stack
          spacing={isDesktop ? 1.25 : 0.75}
          sx={{
            position: "relative",
            zIndex: 2,
            mt: "auto",
            p: isDesktop ? 4 : 1.5,
            pt: isDesktop ? 2 : 1,
          }}
        >
          <Typography
            variant={isDesktop ? "h5" : "subtitle2"}
            fontWeight={800}
            sx={{ color: "grey.900", lineHeight: 1.3 }}
          >
            گردونه شانس
          </Typography>
          <Typography
            variant={isDesktop ? "h6" : "body1"}
            fontWeight={400}
            sx={{ color: "grey.800", lineHeight: 1.45 }}
          >
            بچرخون و کد تخفیف بگیر
          </Typography>

          <Stack direction="row">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: isDesktop ? 1 : 0.5,
                px: isDesktop ? 2 : 1,
                py: isDesktop ? 1 : 0.5,
                borderRadius: 300,
                bgcolor: "secondary.700",
                color: "#fff",
                animation: `${ctaPulse} 2.4s ease-in-out infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            >
              <Typography variant={isDesktop ? "body1" : "button"} sx={{ color: "inherit", fontWeight: 700 }}>
                بچرخون
              </Typography>
              <Icon fontSize={isDesktop ? "medium" : "small"} sx={{ color: "inherit" }}>
                <ArrowLeftIcon />
              </Icon>
            </Box>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
